import type { Entity, Viewer } from '@xeokit/xeokit-sdk';
import {
  buildBoxLinesGeometryFromAABB,
  Mesh,
  PhongMaterial,
  VBOGeometry,
} from '@xeokit/xeokit-sdk';
import { addContextMenuListener } from '@xeokit/xeokit-sdk/src/plugins/lib/html/MenuEvent.js';
import * as actions from '~/utils/viewer/actions';
import { getCanvasPosFromEvent } from '~/utils/viewer/get-canvas-pos-from-event';
import {
  calculatePairwiseDistances,
  checkModelsVisibility,
  selectFittableModels,
} from '~/utils/viewer/models-distance';
//import { MarqueeSelectionTool } from '~/utils/viewer/tools/marquee-selection-tool';

import {
  DataSourceBIM,
  DataSourceGLTF,
  DataSourceLAS,
  DataSourceSTL,
  DataSourceXKT,
  getIfcPropertySets,
  loadModelBIM,
  loadModelGLTF,
  loadModelIFC,
  loadModelLAS,
  loadModelOBJ,
  loadModelPotree,
  loadModelSTL,
  loadModelXKT,
} from '~/utils/viewer/loaders';

import type {
  PotreeLoaderPlugin,
  PotreeMaterial,
  PotreeModel,
  PotreeVisibilityUpdater,
} from '@xeovision/xeokit-potree-plugin';
import { toast } from 'vue-sonner';
import { detectFarAwayObjects } from '~/utils/viewer/detect-far-away-objects';
import { resolveIfcUnitId, type IfcUnit } from '~/utils/viewer/explorer/ifc-units';
import { REVIT_FEET_TO_MODEL_METRES } from '~/utils/viewer/explorer/rvt-tree';
import { adaptStepMetadata } from '~/utils/viewer/explorer/step-tree';
import { detectMetadataFormat } from '~/utils/viewer/metadata-format';
import { getMetaObjectTypes, getTypeByEntityId } from '~/utils/viewer/metadata-graph';
import { MetadataPropertySet } from './use-model-databases';

export const DISABLED_FAR_CLIPPING = 1_000_000;

export const POTREE_MATERIAL_FACTORIES = {
  rgb: 'createRGB',
  intensity: 'createIntensity',
  classification: 'createClassification',
} as const;
export type PotreeMaterialName = keyof typeof POTREE_MATERIAL_FACTORIES;
export const POTREE_MAX_RENDER_LIMIT = 4_000_000;
export const POTREE_DEFAULT_RENDER_LIMIT = 2_000_000;
const POTREE_CACHE_LIMIT = 2 * POTREE_DEFAULT_RENDER_LIMIT;

// xeokit moves the camera by `secsElapsed * mouseWheelDollyRate * dollyDistFactor` per wheel notch,
// where dollyDistFactor is `eyeToSurfaceDistance / dollyProximityThreshold`, floored at
// dollyMinSpeed and replaced by 1.0 while the pointer is over empty space. That floor and that
// fallback are absolute world distances, so a tuning that feels right on a building makes small
// models unusable: STEP goes through a STEP -> GLB -> XKT pipeline and lands in metres, so a part
// is commonly under a metre across and one notch then moves the camera further than the model.
// Scaling the rates with the model keeps a notch worth a constant fraction of what is on screen.
const DOLLY_REFERENCE_DIAG = 60;
const BASE_KEYBOARD_DOLLY_RATE = 100.0;
const BASE_MOUSE_WHEEL_DOLLY_RATE = 100.0;
const BASE_DOLLY_PROXIMITY_THRESHOLD = 30.0;
// Shift + wheel over empty space is the "cross the model quickly" gesture.
const FAST_DOLLY_RATE_MULTIPLIER = 20;

export function resolveCameraFar(
  project?: { cameraFarDisabled?: boolean; cameraFarDistance?: number } | null,
) {
  if (project?.cameraFarDisabled) return DISABLED_FAR_CLIPPING;
  return project?.cameraFarDistance || 5000;
}

export type PropertyObject = {
  id: string;
  name: string;
  type: string;
  propertySets: MetadataPropertySet[];
  attributes: { [key: string]: any };
};

type ExplorerProperty = {
  name?: string;
  value?: unknown;
  valueType?: string;
  ifcPropertyType?: string;
  ifcValueType?: string;
  unit?: number | null;
};
type ExplorerMetaObject = {
  id: string | number;
  name?: string;
  type?: string;
  propertySetIds?: (string | number)[];
  attributes?: Record<string, unknown>;
  [key: string]: unknown;
};
type ExplorerPropertySet = {
  id: string | number;
  name?: string;
  type?: string;
  // Either inline properties or indices into the top-level `properties` array
  properties?: (number | ExplorerProperty)[];
};
type ExplorerRvtElement = {
  Id: string | number;
  Name?: string;
  class?: string;
  ParameterGroups?: number[];
  appearances?: string[];
  [key: string]: unknown;
};
type ExplorerParameterGroup = { Name?: string; Parameters?: number[] };
type ExplorerParameter = {
  Name?: string;
  Value?: unknown;
  ValueString?: string | null;
  Unit?: number | null;
};
type ExplorerMetadata = {
  // Top-level header emitted by the converter alongside the per-object data.
  // Present for IFC; RVT may carry a subset. Surfaced when the Explorer root
  // row is selected so the whole-model information is visible.
  id?: string | number;
  projectId?: string;
  author?: string;
  createdAt?: string;
  schema?: string;
  creatingApplication?: string;
  projectUnits?: Record<string, number>[];

  metaObjects?: ExplorerMetaObject[];
  properties?: ExplorerProperty[];
  propertySets?: ExplorerPropertySet[];
  units?: IfcUnit[];
  Elements?: ExplorerRvtElement[];
  Parameters?: ExplorerParameter[];
  ParameterGroups?: ExplorerParameterGroup[];
  Units?: { Name?: string }[];
  // RVT header: `Title` names the document, `SiteLocation` indexes into `Elements`.
  Title?: string;
  IsFamilyDocument?: boolean;
  SiteLocation?: number;
};

type RvtSiteLocation = {
  PlaceName?: string;
  Latitude?: number;
  Longitude?: number;
  Elevation?: number;
  TimeZone?: number;
};

function stringifyValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

export type ViewerStoreState = {
  isLoading: boolean;
  isInitialized: boolean;
  distanceErrorShown: boolean;
  farAwayModelIds: string[];
  propertiesInspector: {
    loadingIds: string[];
    metaObjects: {
      [key: string]: PropertyObject;
    };
  };
};

type DefaultHighlightMaterialObject = {
  fill: boolean;
  edges: boolean;
  fillAlpha: number;
  edgeAlpha: number;
  edgeColor: number[];
};

export const useViewerStore = defineStore('viewer-store', () => {
  const { locale, t } = useI18n();
  const uiStore = useUiStore();
  const toolbarStore = useToolbarStore();
  const annotationsStore = useViewerAnnotationsStore();

  const {
    $FastNavPlugin,
    $NavCubePlugin,
    $Viewer,
    $ViewCullPlugin,
    $StoreyViewsPlugin,
    $ContextMenu,
    $CameraMemento,
    $math,
    $os,
    $PointerLens,
    $XKTLoaderPlugin,
    $LASLoaderPlugin,
    $OBJLoaderPlugin,
    $STLLoaderPlugin,
    $GLTFLoaderPlugin,
    $DotBIMLoaderPlugin,
    $XeoIFCLoaderPlugin,
    $FpsStats,
  } = useNuxtApp();

  let viewer: InstanceType<typeof $Viewer> | undefined;
  let cameraMemento: InstanceType<typeof $CameraMemento> | undefined;

  let fastNavPlugin: InstanceType<typeof $FastNavPlugin> | undefined;
  let navCubePlugin: InstanceType<typeof $NavCubePlugin> | undefined;

  let storeyViewsPlugin: InstanceType<typeof $StoreyViewsPlugin> | undefined;

  let objectsContextMenu: InstanceType<typeof $ContextMenu> = returnNewObjectsContextMenuObj();
  let canvasContextMenu: InstanceType<typeof $ContextMenu> = returnNewCanvasContextMenuObj();

  let xktLoaderPlugin: InstanceType<typeof $XKTLoaderPlugin> | undefined = undefined;
  let lasLoaderPlugin: InstanceType<typeof $LASLoaderPlugin> | undefined = undefined;
  let gltfLoaderPlugin: InstanceType<typeof $GLTFLoaderPlugin> | undefined = undefined;
  let stlLoaderPlugin: InstanceType<typeof $STLLoaderPlugin> | undefined = undefined;
  let objLoaderPlugin: InstanceType<typeof $OBJLoaderPlugin> | undefined = undefined;
  let bimLoaderPlugin: InstanceType<typeof $DotBIMLoaderPlugin> | undefined = undefined;
  let xeoIfcLoaderPlugin: InstanceType<typeof $XeoIFCLoaderPlugin> | undefined = undefined;
  let potreeLoaderPlugin: InstanceType<typeof PotreeLoaderPlugin> | undefined = undefined;
  const potreeModels = new Map<string, PotreeModel>();
  let potreeVisibilityUpdater: PotreeVisibilityUpdater | undefined = undefined;
  const potreeMaterials = new Map<PotreeMaterialName, PotreeMaterial>();
  const potreeSettings = reactive({
    renderLimit: POTREE_DEFAULT_RENDER_LIMIT,
    material: 'rgb' as PotreeMaterialName,
    modelsCount: 0,
  });

  //let marqueeSelectionTool: InstanceType<typeof MarqueeSelectionTool> | undefined = undefined;
  let pointerLens: InstanceType<typeof $PointerLens> | undefined = undefined;

  let fpsStats: InstanceType<typeof $FpsStats> | undefined;

  let dollyScale = 1;

  let defaultHighlightMaterial: DefaultHighlightMaterialObject;

  const modelMementos = {};
  const tempVec3a = $math.vec3();
  const tempVec3 = $math.vec3();
  const touched = ref(false);
  const state: ViewerStoreState = reactive({
    isLoading: true,
    isInitialized: false,
    distanceErrorShown: false,
    farAwayModelIds: [],
    propertiesInspector: {
      loadingIds: [],
      metaObjects: {},
    },
  });

  // TODO: We should destroy last context menus when changing the locale
  watch(locale, () => {
    canvasContextMenu = returnNewCanvasContextMenuObj();
    objectsContextMenu = returnNewObjectsContextMenuObj();
  });

  function getHelpers() {
    return {
      viewer,
      cameraMemento,
      navCubePlugin,
      storeyViewsPlugin,
      fastNavPlugin,
      pointerLens,
      //marqueeSelectionTool,
      fpsStats,
    };
  }

  function getLoaders() {
    return {
      xktLoaderPlugin,
      lasLoaderPlugin,
      gltfLoaderPlugin,
      stlLoaderPlugin,
      objLoaderPlugin,
      bimLoaderPlugin,
      xeoIfcLoaderPlugin,
      potreeLoaderPlugin,
    };
  }

  function isPotreeModel(entity: unknown) {
    for (const model of potreeModels.values()) if (model === entity) return true;
    return false;
  }

  function registerPotreeModel(id: string, model: PotreeModel) {
    potreeModels.set(id, model);
    potreeSettings.modelsCount = potreeModels.size;
  }

  async function getPotreeMaterial(name: PotreeMaterialName = potreeSettings.material) {
    const cached = potreeMaterials.get(name);
    if (cached) return cached;
    if (!viewer) return undefined;

    const { PotreeLoaderPlugin } = await import('@xeovision/xeokit-potree-plugin');
    const material = PotreeLoaderPlugin.Materials[POTREE_MATERIAL_FACTORIES[name]](viewer.scene);
    potreeMaterials.set(name, material);
    return material;
  }

  async function setPotreeMaterial(name: PotreeMaterialName) {
    potreeSettings.material = name;
    const material = await getPotreeMaterial(name);
    if (!material || potreeSettings.material !== name) return;
    for (const model of potreeModels.values()) model.setSurfaceMaterial(material);
  }

  function setPotreeRenderLimit(points: number) {
    potreeSettings.renderLimit = points;
    potreeVisibilityUpdater?.setVisibilityLimit(points);
  }

  async function ensurePotreeLoaderPlugin() {
    if (!viewer) return undefined;
    if (potreeLoaderPlugin) return potreeLoaderPlugin;

    const { PotreeLoaderPlugin } = await import('@xeovision/xeokit-potree-plugin');
    if (!potreeLoaderPlugin) {
      const updater = new PotreeLoaderPlugin.VisibilityCacheLimitUpdater();
      updater.setVisibilityLimit(potreeSettings.renderLimit);
      updater.setCacheLimit(POTREE_CACHE_LIMIT);
      potreeVisibilityUpdater = updater;
      potreeLoaderPlugin = new PotreeLoaderPlugin(viewer, { visibilityUpdater: updater });
    }
    return potreeLoaderPlugin;
  }

  async function initViewer() {
    const viewerDistanceMeasurementsStore = useViewerDistanceMeasurementsStore();
    const viewerSectionsStore = useViewerSectionsStore();
    //const viewerZonesStore = useViewerZonesStore();
    //const viewerBCFViewpointsStore = useViewerBCFViewpointsStore();

    const projectStore = useProjectStore();
    const route = useRoute();

    function setDtx() {
      return route.query.dtx === 'false' ? false : route.query.dtx === 'true' ? true : false;
    }

    viewer = new $Viewer({
      canvasId: 'xeokit-canvas',
      transparent: true,
      dtxEnabled: setDtx(),
      entityOffsetsEnabled: true,
      saoEnabled: true,
      pbrEnabled: projectStore.project?.enablePbr,
      spinnerElementId: 'xeokit-spinner-element',
    });

    const maxTreeDepth = route.query.maxTreeDepth ? route.query.maxTreeDepth : 20;
    new $ViewCullPlugin(viewer, {
      maxTreeDepth: maxTreeDepth as number,
    });

    defaultHighlightMaterial = {
      fill: viewer.scene.highlightMaterial.fill,
      edges: viewer.scene.highlightMaterial.edges,
      fillAlpha: viewer.scene.highlightMaterial.fillAlpha,
      edgeAlpha: viewer.scene.highlightMaterial.edgeAlpha,
      edgeColor: viewer.scene.highlightMaterial.edgeColor,
    };

    pointerLens = new $PointerLens(viewer, {
      containerId: 'xeokit-pointer-lens',
      lensPosToggle: true,
      lensPosToggleAmount: 150,
      lensPosMarginLeft: 85,
      lensPosMarginTop: 25,
    });

    viewerDistanceMeasurementsStore.init();
    viewerSectionsStore.init();

    // Default camera world axis, Z up
    viewer.camera.worldAxis = [
      1,
      0,
      0, // Right (+X)

      0,
      0,
      1, // Up (+Z)

      0,
      -1,
      0, // Forward (-Y)
    ];

    viewer.camera.eye = [1, 0, 0];
    viewer.camera.up = [0, 0, 1];
    viewer.camera.look = [0, -1, 0];

    // Camera control
    viewer.cameraControl.navMode = 'orbit';
    viewer.cameraControl.panRightClick = true;
    viewer.cameraControl.followPointer = true;
    viewer.cameraControl.doublePickFlyTo = false;
    viewer.cameraControl.smartPivot = false;

    viewer.cameraControl.setCursorStyle(
      'rotate',
      'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLXJvdGF0ZS0zZCI+PHBhdGggZD0iTTE2LjQ2NiA3LjVDMTUuNjQzIDQuMjM3IDEzLjk1MiAyIDEyIDIgOS4yMzkgMiA3IDYuNDc3IDcgMTJzMi4yMzkgMTAgNSAxMGMuMzQyIDAgLjY3Ny0uMDY5IDEtLjIiLz48cGF0aCBkPSJtMTUuMTk0IDEzLjcwNyAzLjgxNCAxLjg2LTEuODYgMy44MTQiLz48cGF0aCBkPSJNMTkgMTUuNTdjLTEuODA0Ljg4NS00LjI3NCAxLjQzLTcgMS40My01LjUyMyAwLTEwLTIuMjM5LTEwLTVzNC40NzctNSAxMC01YzQuODM4IDAgOC44NzMgMS43MTggOS44IDQiLz48L3N2Zz4=") 16 16, move',
    );

    viewer.cameraControl.setCursorStyle(
      'pan',
      'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLW1vdmUiPjxwYXRoIGQ9Ik0xMiAydjIwIi8+PHBhdGggZD0ibTE1IDE5LTMgMy0zLTMiLz48cGF0aCBkPSJtMTkgOSAzIDMtMyAzIi8+PHBhdGggZD0iTTIgMTJoMjAiLz48cGF0aCBkPSJtNSA5LTMgMyAzIDMiLz48cGF0aCBkPSJtOSA1IDMtMyAzIDMiLz48L3N2Zz4=") 16 16, move',
    );

    // Dolly tweaks for best precision when aligning camera for BCF snapshots. The rates are
    // re-derived from the model extents by updateDollySpeed() once something is loaded.
    viewer.cameraControl.keyboardDollyRate = BASE_KEYBOARD_DOLLY_RATE;
    viewer.cameraControl.mouseWheelDollyRate = BASE_MOUSE_WHEEL_DOLLY_RATE;
    viewer.cameraControl.dollyInertia = 0;
    viewer.cameraControl.dollyMinSpeed = 0.04;
    viewer.cameraControl.dollyProximityThreshold = BASE_DOLLY_PROXIMITY_THRESHOLD;

    // X-ray settings
    // @ts-ignore xeokit sdk
    // viewer.scene.xrayMaterial.preset = EmphasisMaterial.presets['sepia'];

    viewer.scene.xrayMaterial.fillAlpha = 0.4;
    viewer.scene.xrayMaterial.fillColor = [
      0.970588207244873, 0.7965892553329468, 0.6660899519920349,
    ];
    viewer.scene.xrayMaterial.edges = true;
    viewer.scene.xrayMaterial.edgeAlpha = 1.0;
    viewer.scene.xrayMaterial.edgeColor = [
      0.529411792755127, 0.4577854573726654, 0.4100345969200134,
    ];
    viewer.scene.xrayMaterial.edgeWidth = 1;

    const cameraPivotElement = document
      .createRange()
      .createContextualFragment(`<div class='xeokit-camera-pivot-marker'></div>`)
      .firstChild as HTMLElement;
    if (cameraPivotElement) {
      document.body.appendChild(cameraPivotElement);
      viewer.cameraControl.pivotElement = cameraPivotElement;
    }

    // viewer.scene.camera.perspective.near = 0.01;
    viewer.scene.camera.perspective.far = resolveCameraFar(projectStore.project);
    // viewer.scene.camera.ortho.near = 0.01;
    viewer.scene.camera.ortho.far = resolveCameraFar(projectStore.project);

    // @ts-ignore
    window.viewer = viewer;

    // TODO: Animated scene for demo ?
    // viewer.scene.on('tick', function () {
    //   viewer!.scene.camera.orbitYaw(0.3);
    //   viewer!.scene.camera.orbitPitch(0.2);
    // });

    xktLoaderPlugin = new $XKTLoaderPlugin(viewer, {
      reuseGeometries: false,
      dataSource: new DataSourceXKT({
        cacheBuster: false,
      }),
    });

    // TODO: Set to true when you need to load multiple instances of the same model
    xktLoaderPlugin.globalizeObjectIds = true;

    lasLoaderPlugin = new $LASLoaderPlugin(viewer, {
      dataSource: new DataSourceLAS({
        cacheBuster: false,
      }),
      //center: true,
      rotate: [-90, 0, 0],
      //skip: 1, // Load every 10th point, or make it dynamic depending on las file size
      // @ts-ignore xeokit
      fp64: false, // Positions expected in 32-bit floats instead of 64-bit
      //colorDepth: 'auto', // Whether colors encoded using 8 or 16 bits - accepted values are "auto", 8 and 16
    });

    gltfLoaderPlugin = new $GLTFLoaderPlugin(viewer, {
      dataSource: new DataSourceGLTF({
        cacheBuster: false,
      }),
      //new $GLTFDefaultDataSource({ cacheBuster: false }),
    });

    stlLoaderPlugin = new $STLLoaderPlugin(viewer, {
      dataSource: new DataSourceSTL({ cacheBuster: false }),
      //new $STLDefaultDataSource({ cacheBuster: false }),
    });

    bimLoaderPlugin = new $DotBIMLoaderPlugin(viewer, {
      //dataSource: new $DotBIMDefaultDataSource({ cacheBuster: false }),
      dataSource: new DataSourceBIM({ cacheBuster: false }),
    });

    objLoaderPlugin = new $OBJLoaderPlugin(viewer, {});

    xeoIfcLoaderPlugin = new $XeoIFCLoaderPlugin(viewer, {});
    potreeLoaderPlugin = undefined;
    potreeVisibilityUpdater = undefined;
    potreeMaterials.clear();
    potreeModels.clear();
    potreeSettings.modelsCount = 0;

    navCubePlugin = new $NavCubePlugin(viewer, {
      canvasId: 'xeokit-canvas-cube',
      visible: true,
      fitVisible: true,
      color: '#CFCFCF',
      synchProjection: true,
      shadowVisible: false,
      cameraFly: true, // Fly camera to each selected axis/diagonal
      cameraFitFOV: 45, // How much field-of-view the scene takes once camera has fitted it to view
      cameraFlyDuration: 0.5,
    });

    createFastNavPlugin();

    storeyViewsPlugin = new $StoreyViewsPlugin(viewer, {
      id: 'storey-views',
    });

    fpsStats = new $FpsStats();
    fpsStats.showPanel(0);

    objectsContextMenu.on('hidden', () => {
      const entity = objectsContextMenu.context.entity;
      entity.highlighted = false;

      if (!viewer) return;

      if (!toolbarStore.selectObjects.active) objectsContextMenu.context.entity.edges = false;
    });

    // @ts-ignore xeokit
    const viewerCanvas = viewer.scene.canvas.canvas as HTMLCanvasElement;

    window.addEventListener('keydown', (e) => {
      if (e.key === 'Shift') {
        viewerCanvas.addEventListener('mousemove', setMouseWheelDollyRate);
        viewerCanvas.addEventListener('wheel', setMouseWheelDollyRate, { passive: true });
      }
    });

    window.addEventListener('keyup', (e) => {
      if (e.key === 'Shift') resetMouseWheelDollyRate();
    });

    // Focus can leave the window mid-gesture (alt-tab), and the Shift keyup then never arrives,
    // which would otherwise leave the fast dolly rate latched on for the rest of the session.
    window.addEventListener('blur', resetMouseWheelDollyRate);

    function isCursorPointingOnObject(e: MouseEvent) {
      if (!viewer) return;
      return viewer.scene.pick({
        canvasPos: [e.offsetX, e.offsetY],
      });
    }

    function setMouseWheelDollyRate(e: MouseEvent) {
      if (!viewer) return;
      const targetIsObject = isCursorPointingOnObject(e);
      viewer.cameraControl.mouseWheelDollyRate = targetIsObject
        ? BASE_MOUSE_WHEEL_DOLLY_RATE * dollyScale
        : BASE_MOUSE_WHEEL_DOLLY_RATE * FAST_DOLLY_RATE_MULTIPLIER * dollyScale;
    }

    function resetMouseWheelDollyRate() {
      if (!viewer) return;
      viewer.cameraControl.mouseWheelDollyRate = BASE_MOUSE_WHEEL_DOLLY_RATE * dollyScale;
      viewerCanvas.removeEventListener('mousemove', setMouseWheelDollyRate);
      viewerCanvas.removeEventListener('wheel', setMouseWheelDollyRate);
    }
    viewerCanvas.addEventListener('touchstart', () => {
      touched.value = true;
    });
    viewerCanvas.addEventListener('touchend', () => {
      touched.value = false;
    });

    if (!$os.isTouchDevice() || !touched.value) {
      viewer.cameraControl.on('rightClick', onRightClickScene);
    } else {
      viewer.cameraControl.on('rightClick', onRightClickScene);

      addContextMenuListener(viewerCanvas, handleContextMenu);
    }

    //marqueeSelectionTool = new MarqueeSelectionTool();

    annotationsStore.init();

    window.addEventListener('keyup', (event) => {
      if (viewer && event.key === 'Escape') {
        viewer.scene.setObjectsSelected(viewer.scene.selectedObjectIds, false);
        state.propertiesInspector.metaObjects = {};
        state.propertiesInspector.loadingIds = [];
      }
    });

    state.isInitialized = true;
  }

  function returnNewCanvasContextMenuObj() {
    return new $ContextMenu({
      enabled: true,
      context: {
        viewer: viewer,
      },
      items: [
        [actions.hideAllAction(), actions.showAllAction()],
        [
          {
            getTitle: () => t('components.xeokit_viewer.context_menu.view') || 'View',
            doAction: () => {},
            items: [[actions.viewFitSelectionAction(), actions.viewFitAllAction()]],
          },
          {
            getTitle: () => t('components.xeokit_viewer.context_menu.x_ray') || 'X-Ray',
            doAction: () => {},
            items: [[actions.xRayAllAction(), actions.xRayNoneAction()]],
          },
        ],
        [
          actions.selectNoneAction(),
          actions.resetViewAction(),
          actions.clearSectionsAction(),
          actions.measurementsAction(),
        ],
      ],
    });
  }

  function returnNewObjectsContextMenuObj() {
    return new $ContextMenu({
      items: [
        // measureDistanceMode is undefined while toolbarStore is still initializing (store cycle)
        toolbarStore.measureDistanceMode?.active === true && [
          actions.endMeasurementAction(),
          actions.cancelMeasurementAction(),
        ],
        [actions.inspectPropertiesAction()],
        [
          {
            getTitle: () => t('components.xeokit_viewer.context_menu.view') || 'View / Show',
            doAction: () => {},
            items: [
              [
                actions.viewFitAction(),
                actions.fitAllAction(),
                actions.viewFitSelectionAction(),
                actions.showAllAction(),
              ],
            ],
          },
          {
            getTitle: () => t('components.xeokit_viewer.context_menu.hide') || 'Hide',
            doAction: () => {},
            items: [
              [actions.hideEntityAction(), actions.hideOthersAction(), actions.hideAllAction()],
            ],
          },
          {
            getTitle: () => t('components.xeokit_viewer.context_menu.x_ray') || 'X-Ray',
            doAction: () => {},
            items: [
              [
                actions.xRayEntityAction(),
                actions.xRayUndoAction(),
                actions.xRayOthersAction(),
                actions.xRayResetAction(),
              ],
            ],
          },
          {
            getTitle: () => t('components.xeokit_viewer.context_menu.select') || 'Select',
            doAction: () => {},
            items: [
              [
                actions.selectEntityAction(),
                actions.selectUndoAction(),
                actions.selectClearAction(),
              ],
            ],
          },
        ],
        [actions.clearSectionsAction(), actions.measurementsAction()],
      ],
      enabled: true,
    });
  }

  function checkFarAwayObjects() {
    const uiStore = useUiStore();

    const viewer = getHelpers().viewer;
    const storeyViewsPlugin = getHelpers().storeyViewsPlugin;

    if (!viewer || !storeyViewsPlugin) return;

    const check = detectFarAwayObjects(viewer, storeyViewsPlugin.storeys);

    if (check.hasObjectsFarAway) {
      console.error(
        `Your model appears to cover a very large area. If nothing is visible, it's possible that objects are placed far from each other. You can try zooming in or exploring manually to locate model parts.`,
      );

      toast.error(t('notifications.far_away_objects.title'), {
        description: t('notifications.far_away_objects.description'),
        duration: Infinity,
        action: {
          label: t('notifications.explorer'),
          onClick: () => {
            uiStore.explorerPanelTab = 'classes';
            uiStore.explorerPanelVisible = true;
          },
        },
      });
    }
  }

  // Keeps a wheel notch worth a constant fraction of the model, so zooming feels the same on a
  // sub-metre STEP part as on a building. See DOLLY_REFERENCE_DIAG.
  function updateDollySpeed() {
    if (!viewer) return;

    let largestDiag = 0;

    for (const model of Object.values(viewer.scene.models)) {
      // @ts-ignore xeokit
      if (model.numEntities === 0 && model.numGeometries === 0) continue;

      const diag = $math.getAABB3Diag(model.aabb);
      if (Number.isFinite(diag) && diag > largestDiag) largestDiag = diag;
    }

    dollyScale = largestDiag > 0 ? largestDiag / DOLLY_REFERENCE_DIAG : 1;

    viewer.cameraControl.keyboardDollyRate = BASE_KEYBOARD_DOLLY_RATE * dollyScale;
    viewer.cameraControl.mouseWheelDollyRate = BASE_MOUSE_WHEEL_DOLLY_RATE * dollyScale;
    viewer.cameraControl.dollyProximityThreshold = BASE_DOLLY_PROXIMITY_THRESHOLD * dollyScale;
  }

  function checkModelsDistance() {
    if (state.distanceErrorShown) return;

    const viewer = getHelpers().viewer;
    if (!viewer) return;

    const modelsAABBs: number[][] = [];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [key, model] of Object.entries(viewer.scene.models)) {
      // @ts-ignore xeokit
      if (model.numEntities === 0 && model.numGeometries === 0) continue;
      modelsAABBs.push(model.aabb);
    }

    for (const potreeModel of potreeModels.values()) modelsAABBs.push(potreeModel.aabb);

    const cameraFarPlaneDistance = toolbarStore.perspectiveOrthoMode.active
      ? viewer.camera.ortho.far
      : viewer.camera.perspective.far;

    const check = checkModelsVisibility(modelsAABBs, cameraFarPlaneDistance);

    if (check.status) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const distances = calculatePairwiseDistances(modelsAABBs);
    } else {
      console.error(
        'Models are too far apart to be visible. Adjust their positions or increase camera far plane.',
      );

      const uiStore = useUiStore();

      toast.error(t('notifications.large_distance'), {
        description: `Models are too far apart, maximum actual distance: ${parseFloat(`${check.actualMaxDistance}`).toFixed(2)}, Max allowable for current camera far plane: ${parseFloat(`${check.maxAllowableDistance}`).toFixed(2)}`,
        duration: Infinity,
        action: {
          label: t('notifications.change'),
          onClick: () => {
            uiStore.explorerPanelTab = 'models';
            uiStore.explorerPanelVisible = true;
          },
        },
      });

      state.distanceErrorShown = true;
    }
  }

  function loadAnnotations() {
    const projectStore = useProjectStore();

    if (projectStore.project?.annotationEntries && !annotationsStore.annotationsLoaded) {
      annotationsStore.loadAnnotations(projectStore.project.annotationEntries);
    }
  }

  function toggleAnnotations() {
    const projectStore = useProjectStore();
    if (projectStore.project?.annotationEntries) {
      annotationsStore.toggleAnnotations();
    }
  }

  async function showObjectProperties(objectId: string) {
    if (!viewer) return;

    const metaObject = viewer.metaScene.metaObjects[objectId];
    if (!metaObject) {
      await showObjectPropertiesFromScene(objectId);
      return;
    }

    showObjectPropertiesLocal(objectId);
  }

  async function showObjectPropertiesLocal(objectId: string) {
    if (!viewer) return;

    const metaObject = viewer.metaScene.metaObjects[objectId];
    if (!metaObject) return;

    const propertyObject: PropertyObject = {
      id: metaObject.id,
      name: metaObject.name,
      type: metaObject.type,
      propertySets: [],
      // `id` carries the model prefix once ids are globalized; the GUID is the original one
      attributes: { GUID: String(metaObject.originalSystemId ?? metaObject.id) },
    };

    uiStore.propertiesPanelVisible = true;
    state.propertiesInspector.loadingIds.push(objectId);

    let i = 0;

    for (const propertySet of metaObject.propertySets) {
      const properties = propertySet.properties.map((property) => {
        i++;

        return {
          id: String(i),
          name: property.name,
          ifcPropertyType: String(property.type),
          ifcValueType: String(property.valueType),
          value: property.value,
          valueType: String(property.valueType),
          unitId: null,
          unitName: null,
        };
      });

      propertyObject.propertySets.push({
        id: propertySet.id,
        name: propertySet.name,
        type: propertySet.type,
        properties: properties,
      });
    }

    if (propertyObject.propertySets.length === 0) {
      propertyObject.propertySets.push(...(await getIfcPropertySets(objectId)));
    }

    // TODO add type in xeokit
    // @ts-ignore
    const attributes = metaObject.attributes;

    if (attributes) {
      Object.assign(propertyObject.attributes, attributes);
    }

    state.propertiesInspector.loadingIds = state.propertiesInspector.loadingIds.filter(
      (id) => id !== objectId,
    );

    if (!viewer.scene.selectedObjectIds.includes(objectId)) return;
    state.propertiesInspector.metaObjects[objectId] = propertyObject;
  }

  function getSceneModelMetadata(sceneModelId: string) {
    const model = viewer?.scene.models[sceneModelId] as unknown as { metadata?: ExplorerMetadata };
    return model?.metadata;
  }

  function buildPropertyObjectFromJson(
    sceneModelId: string,
    sourceId: string,
  ): PropertyObject | undefined {
    const metadata = getSceneModelMetadata(sceneModelId);
    if (!metadata) return undefined;

    const format = detectMetadataFormat(metadata);
    if (format === 'rvt') return buildRvtPropertyObject(metadata, sourceId);
    if (format === 'step') return buildStepPropertyObject(metadata, sourceId);
    // `ifc` and `scene` share the metaObjects/propertySets shape.
    if (Array.isArray(metadata.metaObjects)) return buildIfcPropertyObject(metadata, sourceId);
    return undefined;
  }

  // STEP carries the same metaObjects/propertySets shape as IFC once its raw entity lines are
  // normalized, so the reader is shared rather than duplicated.
  function buildStepPropertyObject(metadata: ExplorerMetadata, sourceId: string) {
    return buildIfcPropertyObject(adaptStepMetadata(metadata), sourceId);
  }

  function buildIfcPropertyObject(metadata: ExplorerMetadata, sourceId: string) {
    const meta = (metadata.metaObjects ?? []).find((o) => String(o.id) === sourceId);
    if (!meta) return undefined;

    const allProperties = metadata.properties ?? [];
    const units = metadata.units ?? [];
    const propertySetsById = new Map(
      (metadata.propertySets ?? []).map((ps) => [String(ps.id), ps] as const),
    );

    const propertySets: MetadataPropertySet[] = [];
    for (const propertySetId of meta.propertySetIds ?? []) {
      const propertySet = propertySetsById.get(String(propertySetId));
      if (!propertySet) continue;

      const properties = (propertySet.properties ?? []).map((entry, index) => {
        const property = typeof entry === 'number' ? allProperties[entry] : entry;
        const unitId = property ? resolveIfcUnitId(units, property) : null;
        const unitName = unitId != null ? (units[unitId]?.name ?? null) : null;
        return {
          id: `${propertySet.id}-${index}`,
          name: property?.name ?? '',
          ifcPropertyType: property?.ifcPropertyType ?? '',
          ifcValueType: property?.ifcValueType ?? '',
          value: stringifyValue(property?.value),
          valueType: property?.valueType ?? '',
          unitId,
          unitName,
        };
      });

      propertySets.push({
        id: String(propertySet.id),
        name: propertySet.name ?? '',
        type: propertySet.type ?? '',
        properties,
      });
    }

    // Everything the converter emitted on the metaObject beyond the structural
    // fields, so new attributes show up without touching this list.
    const structural = new Set(['id', 'name', 'type', 'parent', 'children', 'propertySetIds']);
    const attributes: { [key: string]: unknown } = { GUID: String(meta.id) };
    if (meta.attributes && typeof meta.attributes === 'object')
      Object.assign(attributes, meta.attributes);
    for (const [key, value] of Object.entries(meta)) {
      if (structural.has(key) || value == null || typeof value === 'object') continue;
      attributes[key] = value;
    }

    return {
      id: String(meta.id),
      name: meta.name ?? sourceId,
      type: meta.type ?? '',
      propertySets,
      attributes,
    } satisfies PropertyObject;
  }

  function buildRvtPropertyObject(metadata: ExplorerMetadata, sourceId: string) {
    // `sourceId` is an Entity's originalSystemId, which matches an element's appearance
    // (`<Id>_<n>`) rather than its raw Id.
    const element = (metadata.Elements ?? []).find(
      (e) => String(e.Id) === sourceId || e.appearances?.includes(sourceId),
    );
    if (!element) return undefined;

    const parameterGroups = metadata.ParameterGroups ?? [];
    const parameters = metadata.Parameters ?? [];
    const units = metadata.Units ?? [];

    const propertySets: MetadataPropertySet[] = [];
    for (const groupIndex of element.ParameterGroups ?? []) {
      const group = parameterGroups[groupIndex];
      if (!group) continue;

      const properties = (group.Parameters ?? []).map((parameterIndex, index) => {
        const parameter = parameters[parameterIndex];
        const unitName = parameter?.Unit != null ? (units[parameter.Unit]?.Name ?? null) : null;
        return {
          id: `${groupIndex}-${index}`,
          name: parameter?.Name ?? '',
          ifcPropertyType: '',
          ifcValueType: '',
          value: stringifyValue(parameter?.ValueString ?? parameter?.Value),
          valueType: '',
          unitId: null,
          unitName,
        };
      });

      propertySets.push({ id: `pg-${groupIndex}`, name: group.Name ?? '', type: '', properties });
    }

    const attributes: { [key: string]: unknown } = {};
    const structural = new Set([
      'Id',
      'Name',
      'class',
      'ParameterGroups',
      'Type',
      'Family',
      'Category',
      'Level',
    ]);
    for (const [key, value] of Object.entries(element)) {
      if (structural.has(key) || value == null || typeof value === 'object') continue;
      attributes[key] = value;
    }

    return {
      id: String(element.Id),
      name: element.Name ?? sourceId,
      type: element.class ?? '',
      propertySets,
      attributes,
    } satisfies PropertyObject;
  }

  function buildModelHeaderSet(metadata: ExplorerMetadata): MetadataPropertySet | undefined {
    const site =
      typeof metadata.SiteLocation === 'number'
        ? (metadata.Elements?.[metadata.SiteLocation] as RvtSiteLocation | undefined)
        : undefined;
    const degrees = (radians?: number) =>
      radians == null ? undefined : ((radians * 180) / Math.PI).toFixed(6);

    const fields: [string, unknown, string | null][] = [
      ['File name', metadata.id, null],
      ['Title', metadata.Title, null],
      ['Schema', metadata.schema, null],
      ['Author', metadata.author, null],
      ['Time stamp', metadata.createdAt, null],
      ['Originating system', metadata.creatingApplication, null],
      ['Site', site?.PlaceName, null],
      ['Latitude', degrees(site?.Latitude), '°'],
      ['Longitude', degrees(site?.Longitude), '°'],
      [
        'Site elevation',
        site?.Elevation == null
          ? undefined
          : (site.Elevation * REVIT_FEET_TO_MODEL_METRES).toFixed(2),
        'm',
      ],
      ['Time zone', site?.TimeZone, null],
      ['Family document', metadata.IsFamilyDocument ? 'Yes' : undefined, null],
    ];

    const properties = fields
      .filter(([, value]) => value != null && value !== '')
      .map(([name, value, unitName], index) => ({
        id: `file-header-${index}`,
        name,
        ifcPropertyType: '',
        ifcValueType: '',
        value: stringifyValue(value),
        valueType: '',
        unitId: null,
        unitName,
      }));

    if (properties.length === 0) return undefined;
    return { id: 'file-header', name: 'File Header', type: '', properties };
  }

  function buildModelPropertyObject(sceneModelId: string, fallbackName: string): PropertyObject {
    const metadata = getSceneModelMetadata(sceneModelId);
    if (!metadata) {
      return { id: sceneModelId, name: fallbackName, type: '', propertySets: [], attributes: {} };
    }

    let base: PropertyObject | undefined;

    if (Array.isArray(metadata.metaObjects)) {
      const project = metadata.metaObjects.find((o) => o.type === 'IfcProject');
      if (project) base = buildIfcPropertyObject(metadata, String(project.id));
    }
    // RVT has no project-info element — its whole-model data is the header itself.

    const propertySets: MetadataPropertySet[] = [];
    if (base) propertySets.push(...base.propertySets);
    const headerSet = buildModelHeaderSet(metadata);
    if (headerSet) propertySets.push(headerSet);

    // The IfcProject is shown like any other element; the file header is its own section.
    return {
      id: base?.id ?? String(metadata.id ?? sceneModelId),
      name: base?.name ?? fallbackName,
      type: base?.type ?? '',
      propertySets,
      attributes: base?.attributes ?? {},
    };
  }

  function showModelProperties(sceneModelId: string, fallbackName: string) {
    uiStore.propertiesPanelVisible = true;
    state.propertiesInspector.metaObjects[`model:${sceneModelId}`] = buildModelPropertyObject(
      sceneModelId,
      fallbackName,
    );
  }

  // Opens the panel and stores the node's JSON properties under `key` (scene
  // object id for drawables, the source id otherwise). Always stores an entry so
  // the selected element (its name) is shown even without any metadata.
  function showNodeProperties(key: string, sceneModelId: string, sourceId: string, name: string) {
    uiStore.propertiesPanelVisible = true;
    state.propertiesInspector.metaObjects[key] = buildPropertyObjectFromJson(
      sceneModelId,
      sourceId,
    ) ?? { id: sourceId, name, type: '', propertySets: [], attributes: {} };
  }

  function hideNodeProperties(key: string) {
    delete state.propertiesInspector.metaObjects[key];
  }

  async function showObjectPropertiesFromScene(objectId: string) {
    if (!viewer) return;

    const entity = viewer.scene.objects[objectId] as unknown as {
      originalSystemId?: string;
      model?: { id: string | number };
    };
    if (!entity?.originalSystemId) return;

    const sceneModelId = String(entity.model?.id ?? '');
    const sourceId = entity.originalSystemId;
    uiStore.propertiesPanelVisible = true;

    const propertyObject = buildPropertyObjectFromJson(sceneModelId, sourceId) ?? {
      id: sourceId,
      name: sourceId,
      type: '',
      propertySets: [],
      attributes: {},
    };

    if (!viewer.scene.selectedObjectIds.includes(objectId)) return;
    state.propertiesInspector.metaObjects[objectId] = propertyObject;
  }

  function clearSceneSelections() {
    if (!viewer) return;
    state.propertiesInspector.metaObjects = {};
    state.propertiesInspector.loadingIds = [];
    viewer!.scene.setObjectsSelected(viewer.scene.selectedObjectIds, false);
  }

  function handleContextMenu(event) {
    const canvasPos = getCanvasPosFromEvent(event);
    const hit = viewer?.scene.pick({ canvasPos });

    if (hit && hit.entity?.isObject) {
      if (!viewer) return;

      setDefaultXRayMaterial();
      setSpecialHighlightMaterial();

      hit.entity.highlighted = true;
      hit.entity.edges = true;

      objectsContextMenu.context = {
        viewer,
        entity: hit.entity,
      };

      objectsContextMenu.show(event.pageX, event.pageY);
    } else {
      canvasContextMenu.context = {
        viewer,
      };

      canvasContextMenu.show(event.pageX, event.pageY);
    }
  }

  function onRightClickScene(sceneEvent: {
    event: MouseEvent;
    pagePos: number[];
    canvasPos: number[];
  }) {
    if (viewer) {
      const hit = viewer.scene.pick({
        canvasPos: sceneEvent.canvasPos,
      });

      const viewerDistanceMeasurementsStore = useViewerDistanceMeasurementsStore();
      const distanceMeasurementsContextMenu = viewerDistanceMeasurementsStore.getContextMenu();

      if (viewerDistanceMeasurementsStore.measurementMouseOver) return;
      if (distanceMeasurementsContextMenu.shown) return;

      setDefaultXRayMaterial();
      setSpecialHighlightMaterial();

      if (hit && hit.entity?.isObject) {
        hit.entity.highlighted = true;
        hit.entity.edges = true;

        objectsContextMenu.context = {
          viewer: viewer,
          entity: hit.entity,
        };
        objectsContextMenu.show(sceneEvent.pagePos[0], sceneEvent.pagePos[1]);
      } else {
        canvasContextMenu.context = {
          viewer: viewer,
        };
        canvasContextMenu.show(sceneEvent.pagePos[0], sceneEvent.pagePos[1]);
      }
    }
  }

  function getCameraFarDistance(viewer: Viewer) {
    return toolbarStore.perspectiveOrthoMode.active
      ? viewer.camera.ortho.far
      : viewer.camera.perspective.far;
  }

  function collectModelAABBs(viewer: Viewer) {
    const ids: string[] = [];
    const aabbs: number[][] = [];

    for (const [id, model] of Object.entries(viewer.scene.models)) {
      // @ts-ignore xeokit
      if (model.numEntities === 0 && model.numGeometries === 0) continue;
      ids.push(id);
      aabbs.push(model.aabb);
    }

    for (const [id, potreeModel] of potreeModels) {
      ids.push(id);
      aabbs.push(potreeModel.aabb);
    }

    return { ids, aabbs };
  }

  function updateFarAwayModels() {
    if (!viewer) return;

    const { ids, aabbs } = collectModelAABBs(viewer);
    const selected = new Set(selectFittableModels(aabbs, getCameraFarDistance(viewer)));
    const check = checkModelsVisibility(aabbs, getCameraFarDistance(viewer));

    const previous = state.farAwayModelIds;
    state.farAwayModelIds = check.status ? [] : ids.filter((_, index) => !selected.has(index));

    const recovered = previous.some(
      (id) => ids.includes(id) && !state.farAwayModelIds.includes(id),
    );

    if (recovered) {
      toast.info(t('notifications.model_in_range.title'), {
        description: t('notifications.model_in_range.description'),
        duration: 10000,
        action: {
          label: t('components.xeokit_viewer.toolbar.view_fit'),
          onClick: () => viewFit(),
        },
      });
    }
  }

  function getFittableModelsAABB(viewer: Viewer): number[] | undefined {
    const { aabbs } = collectModelAABBs(viewer);

    if (aabbs.length < 2) return undefined;

    const check = checkModelsVisibility(aabbs, getCameraFarDistance(viewer));
    if (check.status) return undefined;

    const selected = selectFittableModels(aabbs, getCameraFarDistance(viewer));
    const merged = [...aabbs[selected[0]]];

    for (const index of selected.slice(1)) {
      for (let i = 0; i < 3; i++) {
        merged[i] = Math.min(merged[i], aabbs[index][i]);
        merged[i + 3] = Math.max(merged[i + 3], aabbs[index][i + 3]);
      }
    }

    return merged;
  }

  function getVisibleModelsAABB(): number[] | undefined {
    if (!viewer) return undefined;

    const fittable = getFittableModelsAABB(viewer);
    if (fittable) return fittable;

    const scene = viewer.scene;
    const aabbs: number[][] = [];

    if (scene.visibleObjectIds.length > 0) aabbs.push([...scene.getAABB(scene.visibleObjectIds)]);
    for (const { aabb } of potreeModels.values()) aabbs.push(aabb);

    if (aabbs.length === 0) return [...scene.aabb];

    const merged = [...aabbs[0]];
    for (const aabb of aabbs.slice(1)) {
      for (let i = 0; i < 3; i++) {
        merged[i] = Math.min(merged[i], aabb[i]);
        merged[i + 3] = Math.max(merged[i + 3], aabb[i + 3]);
      }
    }

    return merged;
  }

  function viewFit() {
    if (!viewer) return;

    updateFarAwayModels();

    const aabb = getVisibleModelsAABB()!;

    viewer.cameraFlight.flyTo({ aabb: aabb });
    viewer.cameraControl.pivotPos = $math.getAABB3Center(aabb, () => tempVec3);
  }

  // xeokit-sdk's ModelMemento.saveObjects/restoreObjects iterate
  // `metaModel.metaObjects.length` / `metaObjects[i]`, but MetaModel.metaObjects
  // is a `{String:MetaObject}` map — so the loop never runs and the memento is
  // a no-op (e.g. xrayed state never restores on Reset View). We snapshot the
  // SceneModel's entities directly — they're populated regardless of metaModel
  // state and cover every object the user can interact with.
  function saveModelMemento(modelId) {
    const viewer = getHelpers().viewer;
    if (!viewer) return;

    const sceneModel = viewer.scene.models[modelId];
    if (!sceneModel) return;

    const snapshot = {};
    const entities = (sceneModel as any).objects;
    for (const objectId of Object.keys(entities)) {
      const object = entities[objectId];
      if (!object?.isObject) continue;
      snapshot[objectId] = {
        visible: object.visible,
        edges: object.edges,
        xrayed: object.xrayed,
        highlighted: object.highlighted,
        selected: object.selected,
        clippable: object.clippable,
        pickable: object.pickable,
      };
    }

    modelMementos[modelId] = snapshot;
  }

  function restoreModelMemento(modelId) {
    const viewer = getHelpers().viewer;
    if (!viewer) return;

    const snapshot = modelMementos[modelId];
    if (!snapshot) return;

    for (const objectId of Object.keys(snapshot)) {
      const object = viewer.scene.objects[objectId];
      if (!object) continue;
      const entry = snapshot[objectId];
      object.visible = entry.visible;
      object.edges = entry.edges;
      object.xrayed = entry.xrayed;
      object.highlighted = entry.highlighted;
      object.selected = entry.selected;
      object.clippable = entry.clippable;
      object.pickable = entry.pickable;
    }
  }

  function resetCamera() {
    const viewer = getHelpers().viewer;
    if (!viewer) return;

    const scene = viewer.scene;
    const aabb = getVisibleModelsAABB()!;
    const diag = $math.getAABB3Diag(aabb);
    const center = $math.getAABB3Center(aabb, () => tempVec3a);
    const camera = scene.camera;

    const dist = Math.abs(diag / Math.tan(45 * $math.DEGTORAD));
    const dir = $math.normalizeVec3(camera.yUp ? [-0.5, -0.7071, -0.5] : [-1, 1, -1]);
    const up = $math.normalizeVec3(camera.yUp ? [-0.5, 0.7071, -0.5] : [-1, 1, 1]);

    viewer.cameraControl.pivotPos = center;
    viewer.cameraControl.navMode = 'orbit';
    viewer.cameraFlight.flyTo({
      look: center,
      eye: [center[0] - dist * dir[0], center[1] - dist * dir[1], center[2] - dist * dir[2]],
      up: up,
      orthoScale: diag * 1.3,
      projection: 'perspective',
      duration: 1,
    });
  }

  function viewReset() {
    const viewer = getHelpers().viewer;
    if (!viewer) return;

    const scene = viewer.scene;

    // Clear scene-wide overrides first; the per-model snapshot then re-applies
    // the as-loaded state on top. This catches anything that leaked outside
    // the snapshot (e.g. objects not present at saveModelMemento time).
    scene.setObjectsXRayed(scene.xrayedObjectIds, false);
    scene.setObjectsSelected(scene.selectedObjectIds, false);
    scene.setObjectsHighlighted(scene.highlightedObjectIds, false);
    scene.setObjectsVisible(scene.objectIds, true);

    const modelIds = scene.modelIds;
    for (let i = 0; i < modelIds.length; i++) {
      const modelId = modelIds[i];
      restoreModelMemento(modelId);
    }

    resetCamera();
  }

  async function loadModelsFromURLs(
    modelTypes: string[],
    modelURLs: string[],
    modelPositions?: number[],
    bcfURL?: string,
  ) {
    console.log('loadModelsFromURLs', { modelTypes, modelURLs, modelPositions, bcfURL });

    modelTypes.forEach(async (modelType, index) => {
      const modelURL = modelURLs[index];
      const modelFileSize = modelType === 'las' ? await getFileSizeFromURL(modelURL) : 0;
      let positionOverride: number[] | undefined = undefined;

      if (modelPositions && modelPositions[0][index]) {
        positionOverride = [
          modelPositions[0][index],
          modelPositions[1][index],
          modelPositions[2][index],
        ];
      }

      switch (modelType) {
        case 'xkt':
          await loadModelXKT(`xkt-model-${index}`, [modelURL], {
            position: positionOverride,
            bcfURL,
          });
          break;

        case 'glb':
          await loadModelGLTF(`gltf-model-${index}`, modelURL, 'model.gltf', {
            position: positionOverride,
          });
          break;

        case 'ifc':
          await loadModelIFC(`ifc-model-${index}`, modelURL, {
            position: positionOverride,
          });
          break;

        case 'las':
          await loadModelLAS(`las-model-${index}`, modelURL, 'model.las', modelFileSize, {
            position: positionOverride,
          });
          break;

        case 'potree': {
          const [metadataUrl, hierarchyUrl, octreeUrl, ...rest] = modelURL.split(',');

          if (!metadataUrl || !hierarchyUrl || !octreeUrl || rest.length > 0) {
            toast.error('Potree needs three comma separated urls: metadata, hierarchy, octree');
            break;
          }

          await loadModelPotree(`potree-model-${index}`, { metadataUrl, hierarchyUrl, octreeUrl });
          break;
        }

        case 'bim':
          await loadModelBIM(`bim-model-${index}`, modelURL, 'model.bim', {
            position: positionOverride,
          });
          break;

        case 'obj':
          await loadModelOBJ(`obj-model-${index}`, modelURL, 'model.obj', {
            position: positionOverride,
          });
          break;

        case 'stl':
          await loadModelSTL(`stl-model-${index}`, modelURL, 'model.stl', {
            position: positionOverride,
          });
          break;

        default:
          break;
      }

      updateDollySpeed();
    });
  }

  function removeModel(sceneModelId: string) {
    if (!viewer) return;

    const potreeModel = potreeModels.get(sceneModelId);
    if (potreeModel) {
      potreeModel.destroy();
      potreeModels.delete(sceneModelId);
      potreeSettings.modelsCount = potreeModels.size;
      updateDollySpeed();
      updateFarAwayModels();
      return;
    }

    const sceneModel = viewer.scene.models[sceneModelId];
    if (!sceneModel) return;

    // @ts-ignore xeokit sdk
    fastNavPlugin?._onStopped?.();

    sceneModel.destroy();
    removeModelBoundingBox(sceneModelId);
    updateDollySpeed();
    updateFarAwayModels();
  }

  function removeModelBoundingBox(sceneModelId: string) {
    if (!viewer) return;

    if (viewer.scene.objects[`${sceneModelId}#boundingBox`]) {
      viewer.scene.objects[`${sceneModelId}#boundingBox`].destroy();
    }
  }

  function drawModelBoundingBox(sceneModelId: string, aabb: number[]) {
    if (!viewer) return;

    if (!viewer.scene.objects[`${sceneModelId}#boundingBox`]) {
      new Mesh(viewer.scene, {
        id: `${sceneModelId}#boundingBox`,
        pickable: false,
        clippable: false,
        isObject: true,
        geometry: new VBOGeometry(viewer.scene, buildBoxLinesGeometryFromAABB({ aabb })),
        material: new PhongMaterial(viewer.scene, { emissive: [1, 0, 0] }),
      });
    }
  }

  // sets the default styles from xeokit default EmphasisMaterial
  function setDefaultHighlightMaterial() {
    if (!viewer) return;
    viewer.scene.highlightMaterial.fill = defaultHighlightMaterial.fill;
    viewer.scene.highlightMaterial.edges = defaultHighlightMaterial.edges;
    viewer.scene.highlightMaterial.fillAlpha = defaultHighlightMaterial.fillAlpha;
    viewer.scene.highlightMaterial.edgeAlpha = defaultHighlightMaterial.edgeAlpha;
    viewer.scene.highlightMaterial.edgeColor = defaultHighlightMaterial.edgeColor;
  }

  function setDefaultXRayMaterial() {
    if (!viewer) return;
    viewer.scene.xrayMaterial.fillAlpha = 0.4;
    viewer.scene.xrayMaterial.fillColor = [
      0.970588207244873, 0.7965892553329468, 0.6660899519920349,
    ];
    viewer.scene.xrayMaterial.edges = true;
    viewer.scene.xrayMaterial.edgeAlpha = 1.0;
    viewer.scene.xrayMaterial.edgeColor = [
      0.529411792755127, 0.4577854573726654, 0.4100345969200134,
    ];
    viewer.scene.xrayMaterial.edgeWidth = 1;
  }

  // sets the style used for selection hover and right click on entity
  function setSpecialHighlightMaterial() {
    if (!viewer) return;
    viewer.scene.highlightMaterial.fill = true;
    viewer.scene.highlightMaterial.edges = true;
    viewer.scene.highlightMaterial.fillAlpha = 0.1;
    viewer.scene.highlightMaterial.edgeAlpha = 1;
    viewer.scene.highlightMaterial.edgeColor = [1, 1, 0];
  }

  function setSpecialXRayMaterial() {
    if (!viewer) return;
    viewer.scene.xrayMaterial.fillAlpha = 0.25;
    viewer.scene.xrayMaterial.fillColor = [0.4, 0.3, 0.9];
    viewer.scene.xrayMaterial.edgeAlpha = 1;
    viewer.scene.xrayMaterial.edgeColor = [0.5, 0.5, 0.5];
    viewer.scene.xrayMaterial.edgeWidth = 2;
  }

  function updateViewerCameraSettings() {
    if (!viewer) return;

    const projectStore = useProjectStore();

    viewer.scene.camera.perspective.far = resolveCameraFar(projectStore.project);
    viewer.scene.camera.ortho.far = resolveCameraFar(projectStore.project);
  }

  function createFastNavPlugin() {
    if (!viewer) return;

    const fastNavOptions =
      uiStore.fastNavPreset === 'custom'
        ? uiStore.fastNavOptions
        : fastNavPresets[uiStore.fastNavPreset];

    fastNavPlugin = new $FastNavPlugin(viewer, {
      ...fastNavOptions,
      // @ts-ignore
      onMoved: fastNavElementsWithTypes,
    });
  }

  function setFastNavEnabled(enabled: boolean) {
    if (!viewer || enabled === !!fastNavPlugin) return;

    if (enabled) {
      createFastNavPlugin();
      return;
    }

    // @ts-ignore xeokit sdk - reverts the objects FastNav hid while moving
    fastNavPlugin?._onStopped?.();
    fastNavPlugin?.destroy();
    fastNavPlugin = undefined;

    // @ts-ignore xeokit sdk
    const renderer = viewer.scene._renderer;
    renderer.setEdgesEnabled(true);
    renderer.setColorTextureEnabled(true);
    renderer.setPBREnabled(true);
    renderer.setSAOEnabled(true);
    renderer.setTransparentEnabled(true);
    // @ts-ignore xeokit sdk
    viewer.scene.canvas.resolutionScale = 1;
  }

  function fastNavElementsWithTypes() {
    if (!viewer) return;

    const mode = uiStore.fastNavOptions.selectedIfcTypes;
    const ifcTypes = uiStore.fastNavOptions.filterIfcTypes;

    let elementsWithChangedVisibility: Entity[] = [];

    if (ifcTypes && ifcTypes.length > 0) {
      const typeMap = getTypeByEntityId(viewer);
      const objects: { [key: string]: Entity } = viewer.scene.objects;
      for (const [key, value] of Object.entries(objects)) {
        if (!typeMap.has(key)) continue;

        const type = typeMap.get(key);
        const typeMatchesFilter = !!type && ifcTypes.includes(type);

        if (mode === 'show' ? !typeMatchesFilter : typeMatchesFilter) {
          if (value.visible) {
            value.visible = false;
            elementsWithChangedVisibility.push(value);
          }
        }
      }
    }

    return function revert() {
      elementsWithChangedVisibility.forEach((element) => {
        if (viewer?.scene.objects[element.id] === element) element.visible = true;
      });
      elementsWithChangedVisibility = [];
    };
  }

  function getCurrentIfcTypes() {
    if (!viewer) return [];

    return getMetaObjectTypes(viewer);
  }

  return {
    ...toRefs(state),
    clearSceneSelections,
    updateFarAwayModels,
    getHelpers,
    getLoaders,
    initViewer,
    showObjectProperties,
    showNodeProperties,
    showModelProperties,
    hideNodeProperties,
    viewFit,
    viewReset,
    toggleAnnotations,
    loadAnnotations,
    saveModelMemento,
    loadModelsFromURLs,
    ensurePotreeLoaderPlugin,
    registerPotreeModel,
    getPotreeMaterial,
    setPotreeMaterial,
    setPotreeRenderLimit,
    potreeSettings,
    isPotreeModel,
    removeModel,
    checkModelsDistance,
    checkFarAwayObjects,
    drawModelBoundingBox,
    removeModelBoundingBox,
    setDefaultHighlightMaterial,
    setSpecialHighlightMaterial,
    setSpecialXRayMaterial,
    setDefaultXRayMaterial,
    updateViewerCameraSettings,
    updateDollySpeed,
    setFastNavEnabled,
    getCurrentIfcTypes,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useViewerStore, import.meta.hot));
}
